const m = require('minthril');
const { component } = require('../../component');
const { Breadcrumbs } = require('../../components/breadcrumbs');
const { decideAlphaAccess, loadAccounts } = require('../../controllers/admin');

const accountsPerPage = 50;

const AdminPage = component((vnode, emit) => {
  let accounts = [];
  let error = null;
  let requestedUserId = null;
  let loading = false;
  let search = '';
  let currentPageToken = null;
  let nextPageToken = null;
  let previousPageTokens = [];
  let updatingAccountId = null;

  async function load(user, pageToken = currentPageToken, resetPagination = false) {
    if (resetPagination) {
      currentPageToken = null;
      previousPageTokens = [];
      pageToken = null;
    }

    requestedUserId = user.uid;
    loading = true;
    error = null;
    emit('redraw');

    try {
      const result = await loadAccounts(user, search, pageToken);
      accounts = result.accounts;
      currentPageToken = pageToken;
      nextPageToken = result.nextPageToken;
    } catch (reason) {
      console.error('Unable to load administrator accounts.', reason);
      error = reason.message;
    } finally {
      loading = false;
      emit('redraw');
    }
  }

  function searchAccounts(event, user) {
    event.preventDefault();
    load(user, null, true);
  }

  function loadNextPage(user) {
    previousPageTokens = [...previousPageTokens, currentPageToken];
    load(user, nextPageToken);
  }

  function loadPreviousPage(user) {
    const pageToken = previousPageTokens[previousPageTokens.length - 1] || null;
    previousPageTokens = previousPageTokens.slice(0, -1);
    load(user, pageToken);
  }

  function paginationLabel() {
    if (!accounts.length) {
      return 'No accounts';
    }

    const start = previousPageTokens.length * accountsPerPage + 1;
    const end = start + accounts.length - 1;
    return `Accounts ${start}–${end} · page ${previousPageTokens.length + 1}`;
  }

  async function decideAccess(user, account, status) {
    updatingAccountId = account.uid;
    error = null;
    emit('redraw');

    try {
      await decideAlphaAccess(user, account.uid, status);
      await load(user);
    } catch (reason) {
      console.error('Unable to update alpha access.', reason);
      error = reason.message;
    } finally {
      updatingAccountId = null;
      emit('redraw');
    }
  }

  return {
    view: ({ attrs: { user, onStartFreshAccountPreview, startingFreshAccountPreview } }) => {
      if (!loading && requestedUserId !== user.uid) {
        load(user, null, true);
      }

      return m('section.panel.admin-page',
        m(Breadcrumbs, { items: [{ label: 'Admin' }] }),
        m('h2', 'Administrator accounts'),
        m('nav.admin-page__navigation', [
          m('a.button', { href: '/admin' }, 'Accounts'),
          m('a.button', { href: '/admin/usage' }, 'Platform usage'),
          m('a.button', { href: '/admin/estimates' }, 'Estimate checks')
        ]),
        m('section.admin-page__fresh-preview', [
          m('div', [
            m('h3', 'Test the new-account experience'),
            m('p.muted', 'Open the app as a real temporary user. You can create sites and use the normal flows; exiting deletes the account and its data. Abandoned previews expire after 24 hours and are removed by the scheduled cleanup.')
          ]),
          m('button.button', {
            type: 'button',
            disabled: startingFreshAccountPreview,
            onclick: onStartFreshAccountPreview
          }, startingFreshAccountPreview ? 'Starting preview…' : 'Preview as a new user')
        ]),
        m('form.admin-search', {
          onsubmit: event => searchAccounts(event, user)
        },
        m('input', {
          placeholder: 'Search email, name, or user ID',
          value: search,
          oninput: event => {
            search = event.target.value;
          }
        }),
        m('button.button', { type: 'submit', disabled: loading }, 'Search')
        ),
        error && m('p.error', error),
        loading ? m('p.muted', 'Loading accounts…') :
          m('div.admin-accounts', accounts.map(account => m('article.admin-account',
            m('h3', m('a', {
              href: `/admin/accounts/${encodeURIComponent(String(account.uid))}`
            }, account.displayName || account.email || account.uid)),
            account.email && m('p', account.email),
            m('code', account.uid),
            account.requestedAlphaAccess && m('p.admin-request', `Alpha access: ${account.alphaAccessStatus}`),
            account.requestedAlphaAccess && account.alphaAccessStatus === 'pending' && m('div.admin-actions',
              m('button.button', {
                type: 'button',
                disabled: updatingAccountId === account.uid,
                onclick: () => decideAccess(user, account, 'approved')
              }, 'Approve'),
              m('button.button.button--warning', {
                type: 'button',
                disabled: updatingAccountId === account.uid,
                onclick: () => decideAccess(user, account, 'rejected')
              }, 'Reject')
            ),
            m('a', {
              href: `/admin/accounts/${encodeURIComponent(String(account.uid))}`
            }, 'View account and sites')
          ))),
        m('nav.admin-pagination',
          m('button.button', {
            type: 'button',
            disabled: loading || previousPageTokens.length === 0,
            onclick: () => loadPreviousPage(user)
          }, 'Previous'),
          m('span.admin-pagination__status.muted', paginationLabel()),
          m('button.button', {
            type: 'button',
            disabled: loading || !nextPageToken,
            onclick: () => loadNextPage(user)
          }, 'Next')
        )
      );
    }
  };
});

module.exports = { AdminPage };
